/**
 * AgentOsDemoHelpdesk.jsx — public sandbox for the Helpdesk Agent.
 */
import { useState } from "react";
import axios from "axios";
import { API } from "../config";
import {
  Sparkles, ArrowRight, Loader2, Copy, CheckCircle, Lock, MessageSquare, AlertTriangle,
} from "lucide-react";
import AgentOsDemoShell from "./AgentOsDemoShell";

const VOICES = [
  { id: "warm", name: "Vrúcny" }, { id: "formal", name: "Formálny" },
  { id: "friendly", name: "Priateľský" }, { id: "premium", name: "Premium" },
];
const LANGS = [
  { id: "sk", name: "Slovenčina" }, { id: "cs", name: "Čeština" },
  { id: "en", name: "English" }, { id: "de", name: "Deutsch" },
  { id: "pl", name: "Polski" }, { id: "hu", name: "Magyar" },
];
const CATEGORIES = [
  { id: "", name: "Auto-detect" },
  { id: "shipping", name: "Doprava" }, { id: "return", name: "Vrátenie" },
  { id: "refund", name: "Refund" }, { id: "defective", name: "Poškodený produkt" },
  { id: "missing_item", name: "Chýbajúci produkt" }, { id: "payment", name: "Platba" },
  { id: "account", name: "Účet" }, { id: "technical", name: "Technický" },
];

const EXAMPLES = [
  {
    label: "Dlhá doprava",
    ticket_message: "Dobrý deň, objednávku #12345 som zaplatil pred 10 dňami a stále nedorazila. Trackovacie číslo je zaseknuté na 'odoslané'. Toto je nepríjemné, chcem refund.",
    customer_name: "Karol",
    category_hint: "shipping",
  },
  {
    label: "Poškodený tovar",
    ticket_message: "Včera mi dorazila bunda Stormproof veľkosť L, ale zips je rozbitý. Mám použiť vrátenie alebo mi pošlete novú?",
    customer_name: "Janka",
    category_hint: "defective",
  },
  {
    label: "GDPR žiadosť",
    ticket_message: "Dobrý deň, podľa GDPR žiadam o vymazanie všetkých mojich osobných údajov z vašej databázy. Email: jan@example.com",
    customer_name: "Ján",
    category_hint: "account",
  },
];

const SEVERITY_BADGE = {
  low:      { color: "bg-emerald-900/30 text-emerald-300 border-emerald-700/40", label: "low" },
  medium:   { color: "bg-amber-900/30 text-amber-300 border-amber-700/40", label: "medium" },
  high:     { color: "bg-rose-900/30 text-rose-300 border-rose-700/40", label: "high" },
  critical: { color: "bg-rose-950/60 text-rose-200 border-rose-500/40", label: "CRITICAL" },
};

export default function AgentOsDemoHelpdesk() {
  const [form, setForm] = useState({
    ticket_message: "", customer_name: "", brand_name: "",
    brand_voice: "warm", category_hint: "", order_context: "", lang: "sk",
  });
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState({ loading: false, error: null });
  const [copied, setCopied] = useState(false);

  const loadExample = (i) => { setForm((f) => ({ ...f, ...EXAMPLES[i] })); setResult(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.ticket_message.trim()) return;
    setStatus({ loading: true, error: null }); setResult(null);
    try {
      const r = await axios.post(`${API}/agent-os/demo/helpdesk`, form);
      setResult(r.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setStatus({ loading: false, error: typeof d === "object" ? d : { message: d || "Skús to ešte raz." } });
      return;
    }
    setStatus({ loading: false, error: null });
  };

  const copyReply = async () => {
    if (!result?.output?.reply) return;
    try { await navigator.clipboard.writeText(result.output.reply); setCopied(true); } catch {}
    setTimeout(() => setCopied(false), 2000);
  };

  const Left = (
    <form onSubmit={submit} className="space-y-4 p-6 bg-black border border-gray-800">
      <h2 className="text-sm font-black uppercase tracking-widest text-gray-400 mb-3 flex items-center gap-2">
        <MessageSquare className="w-4 h-4" /> Inbound ticket
      </h2>

      <div className="flex flex-wrap gap-2">
        <span className="text-xs uppercase tracking-widest text-gray-500 self-center mr-2">Príklad:</span>
        {EXAMPLES.map((ex, i) => (
          <button key={i} type="button" onClick={() => loadExample(i)}
            data-testid={`agent-os-demo-hd-example-${i}`}
            className="px-3 py-1 text-xs bg-[#0a0a0a] border border-gray-800 hover:border-emerald-700/60 text-gray-300 hover:text-emerald-300">
            {ex.label}
          </button>
        ))}
      </div>

      <textarea required rows={6} placeholder="Skopíruj sem text ticket-u od zákazníka *"
        value={form.ticket_message} onChange={(e) => setForm({ ...form, ticket_message: e.target.value })}
        data-testid="agent-os-demo-hd-ticket"
        className="w-full px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white placeholder-gray-600 leading-relaxed" />

      <div className="grid grid-cols-2 gap-3">
        <input type="text" placeholder="Meno zákazníka"
          value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
          data-testid="agent-os-demo-hd-customer"
          className="px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white placeholder-gray-600" />
        <select value={form.category_hint} onChange={(e) => setForm({ ...form, category_hint: e.target.value })}
          data-testid="agent-os-demo-hd-category"
          className="px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white">
          {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      <textarea rows={2} placeholder="Order context (voliteľné: # objednávky, suma, produkty)"
        value={form.order_context} onChange={(e) => setForm({ ...form, order_context: e.target.value })}
        data-testid="agent-os-demo-hd-context"
        className="w-full px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white placeholder-gray-600" />

      <hr className="border-gray-800" />
      <h3 className="text-sm font-black uppercase tracking-widest text-gray-400">Tvoj brand</h3>
      <input type="text" placeholder="Názov e-shopu"
        value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
        data-testid="agent-os-demo-hd-brand"
        className="w-full px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white placeholder-gray-600" />

      <div className="grid grid-cols-2 gap-3">
        <select value={form.brand_voice} onChange={(e) => setForm({ ...form, brand_voice: e.target.value })}
          data-testid="agent-os-demo-hd-voice"
          className="px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white">
          {VOICES.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select value={form.lang} onChange={(e) => setForm({ ...form, lang: e.target.value })}
          data-testid="agent-os-demo-hd-lang"
          className="px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white">
          {LANGS.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>

      <button type="submit" disabled={status.loading || !form.ticket_message.trim()}
        data-testid="agent-os-demo-hd-submit"
        className="w-full px-5 py-3 bg-emerald-400 hover:bg-emerald-300 disabled:opacity-40 disabled:cursor-not-allowed text-black font-black uppercase tracking-widest text-sm flex items-center justify-center gap-2">
        {status.loading
          ? <><Loader2 className="w-4 h-4 animate-spin" /> Píšem odpoveď…</>
          : <>Vygeneruj odpoveď <Sparkles className="w-4 h-4" /></>}
      </button>
    </form>
  );

  const sev = result?.output?.severity && SEVERITY_BADGE[result.output.severity];

  const Right = (
    <div className="space-y-4">
      <div className="p-6 bg-gradient-to-br from-emerald-950/30 to-black border-2 border-emerald-700/40 min-h-[300px]">
        <h2 className="text-sm font-black uppercase tracking-widest text-emerald-300 mb-3">AI Branded Reply</h2>

        {!result && !status.loading && !status.error && (
          <div className="text-center py-12 text-gray-500 text-sm" data-testid="agent-os-demo-hd-empty">
            <Sparkles className="w-8 h-8 mx-auto mb-3 text-gray-700" /> Vlož ticket a klikni „Vygeneruj odpoveď"
          </div>
        )}
        {status.loading && (
          <div className="text-center py-12 text-emerald-300 text-sm" data-testid="agent-os-demo-hd-loading">
            <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin" /> AI píše odpoveď…
          </div>
        )}
        {status.error && (
          <div className="p-4 bg-rose-950/30 border border-rose-700/40 text-rose-200 text-sm" data-testid="agent-os-demo-hd-error">
            <div className="font-bold mb-1">{status.error?.message || "Niečo zlyhalo"}</div>
            {status.error?.cta_url && (
              <a href={status.error.cta_url} className="inline-flex items-center gap-1 mt-2 text-emerald-300 underline text-xs font-bold">
                Registruj sa zadarmo <ArrowRight className="w-3 h-3" />
              </a>
            )}
          </div>
        )}

        {result?.output && (
          <div data-testid="agent-os-demo-hd-result" className="space-y-4">
            <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-widest">
              {sev && (
                <span className={`px-2 py-1 border font-bold ${sev.color}`} data-testid="agent-os-demo-hd-severity">
                  {sev.label}
                </span>
              )}
              <span className="px-2 py-1 bg-blue-900/30 text-blue-300 border border-blue-700/40 font-bold" data-testid="agent-os-demo-hd-cat">
                {result.output.ticket_category}
              </span>
              {result.output.escalation_needed && (
                <span className="px-2 py-1 bg-rose-950/50 text-rose-200 border border-rose-500/40 font-bold flex items-center gap-1" data-testid="agent-os-demo-hd-escalate">
                  <AlertTriangle className="w-3 h-3" /> Escalate to human
                </span>
              )}
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold mb-1">Odpoveď zákazníkovi</div>
              <div className="p-3 bg-black/50 border border-emerald-900/40 text-white text-sm whitespace-pre-line leading-relaxed" data-testid="agent-os-demo-hd-reply">
                {result.output.reply}
              </div>
            </div>

            {result.output.suggested_internal_note && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold mb-1">🔒 Internal note (skrytá pred zákazníkom)</div>
                <div className="p-3 bg-amber-950/20 border border-amber-700/30 text-amber-200 text-xs italic" data-testid="agent-os-demo-hd-note">
                  {result.output.suggested_internal_note}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-widest">
              <span className="px-2 py-1 bg-gray-900 text-gray-400">{result.latency_ms} ms</span>
              {result.used_fallback && <span className="px-2 py-1 bg-amber-900/30 text-amber-300 font-bold">Fallback</span>}
              <span className="px-2 py-1 bg-gray-900 text-gray-400">Zostáva dnes: {result.remaining_today}</span>
            </div>
            <div className="flex gap-2">
              <button onClick={copyReply} data-testid="agent-os-demo-hd-copy"
                className="flex-1 px-4 py-2 border border-gray-700 hover:border-emerald-400 text-xs font-bold uppercase tracking-widest text-white flex items-center justify-center gap-2">
                {copied ? <><CheckCircle className="w-3 h-3 text-emerald-400" /> Skopírované</> : <><Copy className="w-3 h-3" /> Kopírovať</>}
              </button>
              <a href="/agent-os/onboard" data-testid="agent-os-demo-hd-cta"
                className="flex-1 px-4 py-2 bg-emerald-400 hover:bg-emerald-300 text-black text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2">
                Aktivovať <ArrowRight className="w-3 h-3" />
              </a>
            </div>
          </div>
        )}
      </div>

      <div className="p-4 bg-black border border-gray-800 text-xs text-gray-500 flex gap-3 items-start">
        <Lock className="w-4 h-4 shrink-0 mt-0.5 text-gray-600" />
        <div>Demo má limit 3 generácie / IP / 24h. <a href="/privacy" className="text-emerald-400 underline ml-1">Privacy →</a></div>
      </div>
    </div>
  );

  return (
    <AgentOsDemoShell
      testid="agent-os-demo-helpdesk-page"
      h1={<>Helpdesk <span className="text-emerald-400">Agent</span></>}
      lead="Vlož text ticket-u → AI vygeneruje branded odpoveď, klasifikuje kategóriu, určí severity a flagne ak treba ľudský follow-up. 3 pokusy zadarmo."
      left={Left}
      right={Right}
    />
  );
}
